import { ChangeDetectorRef, ComponentFactoryResolver, ViewContainerRef } from '@angular/core';
import { AbcCore, ObjectHelper } from '@barthauer/abc/core';
import { CacheStorageAdapter } from '@barthauer/abc/core/data';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataEntity, DataEntityInfo, MapLayer, MapLayerType } from '..';
import { ChoicemenuComponent } from './controls/choicemenu/choicemenu.component';
import { ContextmenuComponent } from './controls/contextmenu/contextmenu.component';
import { MapLoader } from './map.loader';
import { MapSelectionPath } from './map.selection-path';
import { ContextMenuButton, ContextMenuConfiguration, RadialMenuOrientation } from './models/contextmenu.model';
import { MapService } from './services/map.service';

declare var ol: any;

export class ContextMenuController {
  private dicContextMenus: Map<string, ContextmenuComponent> = new Map<string, ContextmenuComponent>();
  private currentContextMenu: ContextmenuComponent;
  private choiceMenu: ChoicemenuComponent;
  private currentEntity: DataEntityInfo;
  private popup: any;
  private NO_BASYS_OBJECTS_FOUND = 'Kein BaSYS-Objekt gefunden!';
  public active: boolean = true;
  private commandSelected: boolean;
  private oldcursor: any;
  private selectPath: MapSelectionPath;

  constructor(
    private componentResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private contextMenuConfiguration: ContextMenuConfiguration,
    private changeDetectorRef: ChangeDetectorRef,
    private abcCore: AbcCore,
    private mapService: MapService,
    private map: any,
  ) {
    this.map.getViewport().addEventListener('click', (_) => this.clickMap(_));
    this.mapService.onStartNetworkPathSelection().subscribe((item) => {
      this.selectPathEntities(item.entity, item.includeNodes);
    });
  }

  destroy(): void {
    this.map.getViewport().removeEventListener('click', (_) => this.clickMap(_));
  }

  private selectPathEntities(startEntity: DataEntity, includeNodes: boolean): void {
    this.selectPath = new MapSelectionPath(
      this.componentResolver,
      this.viewContainerRef,
      this.changeDetectorRef,
      this.abcCore,
      this.mapService,
      startEntity,
      () => (this.selectPath = null),
    );
    this.selectPath.selectPathEntities(includeNodes);
  }

  private clickMap(evt: any): void {
    evt.preventDefault();
    if (this.active) {
      const coordinate = this.map.getEventCoordinate(evt);
      this.oldcursor = this.map.getTargetElement()?.style?.cursor;
      if (this.oldcursor != null) {
        this.map.getTargetElement().style.cursor = 'wait';
      }
      const entity = this.selectOneDataEntity(evt, coordinate);

      if (this.selectPath) {
        this.selectPath.nextEntitySelected(entity);
        if (entity) {
          this.resetCursor();
        }
        this.closePopup();
      }
    }
  }

  private openRadialMenu(entity: DataEntityInfo, coordinate: any) {
    if (entity && this.clickMap && this.popup && this.currentContextMenu) {
      this.commandSelected = false;
      this.mapService.navigateToObjectsWithoutZoom([entity.entity]);

      this.currentContextMenu.openRadialMenu();
      this.popup.setPosition(coordinate);
      this.popup.changed();
    }
  }

  private updateContextMenu(entity: DataEntityInfo, useLayer: boolean): Observable<boolean> {
    const lastEntity = this.currentEntity;
    this.currentEntity = entity;

    if (
      lastEntity?.entity?.objectType === entity.entity.objectType &&
      lastEntity?.entity?.feature?.layerdefinition?.target?.url ===
        entity.entity?.feature?.layerdefinition?.target?.url &&
      this.currentContextMenu
    ) {
      this.updatePopup();
      return of(true);
    } else {
      return this.getContextMenuByObjectType(
        entity.entity.objectType,
        entity.entity.feature?.layerdefinition,
        useLayer,
      ).pipe(
        map((contextMenu) => {
          if (contextMenu) {
            this.currentContextMenu = contextMenu;
            this.updatePopup();
            return true;
          }
          return false;
        }),
      );
    }
  }

  private getContextMenuByObjectType(
    objectType: string | number,
    layerdefinition: MapLayer,
    useLayer: boolean,
  ): Observable<ContextmenuComponent> {
    const key = objectType + (layerdefinition?.target?.url ?? '') + useLayer;
    const contextMenu: ContextmenuComponent = this.dicContextMenus.get(key);
    if (!contextMenu) {
      return (
        useLayer
          ? of(this.getLayerContextMenuButtonsFromConfiguration(layerdefinition))
          : this.getContextMenuButtonsFromConfiguration(objectType, layerdefinition)
      ).pipe(
        map((buttons) => {
          const newContextMenu = this.createContextMenu(buttons);
          this.removePopup();
          this.popup = this.createPopup(newContextMenu);
          this.map.addOverlay(this.popup);

          this.dicContextMenus.set(key, newContextMenu);

          return newContextMenu;
        }),
      );
    }

    return of(contextMenu);
  }

  private removePopup() {
    if (this.popup && this.map) this.map.removeOverlay(this.popup);
  }

  private createContextMenu(buttons: ContextMenuButton[]): ContextmenuComponent {
    const contextMenu = this.loadMapContextMenu();
    contextMenu.radClick.subscribe((e) => {
      this.onContextMenuClicked(e);
    });

    contextMenu.diameterPixels = 180;
    contextMenu.backgroundVisible = true;
    contextMenu.buttons = buttons;
    this.changeDetectorRef.detectChanges();

    contextMenu.customizeSubmenus();
    contextMenu.orientation = RadialMenuOrientation.radial;
    contextMenu.contextMenuClosed.subscribe(() => this.onContextMenuClosed());

    return contextMenu;
  }

  private onContextMenuClosed() {
    this.closePopup();
  }

  private onContextMenuClicked(arg: any) {
    const btn: ContextMenuButton = arg.button;

    if (btn.enabled) this.raiseMapAction(this.currentEntity.entity, btn);
  }

  private raiseMapAction(entity: DataEntity, btn: any) {
    if (entity) {
      this.commandSelected = true;
      btn.action(entity);
    } else this.mapService.sendMapShortMessage(this.NO_BASYS_OBJECTS_FOUND);
  }

  private loadMapContextMenu(): ContextmenuComponent {
    const fcFactory = this.componentResolver.resolveComponentFactory(ContextmenuComponent);
    const viewContainerRef = this.viewContainerRef;

    const contextMenuComponentRef = viewContainerRef.createComponent(fcFactory);
    this.changeDetectorRef.detectChanges();

    return <ContextmenuComponent>contextMenuComponentRef.instance;
  }

  private loadChoiceMenu(): ChoicemenuComponent {
    const fcFactory = this.componentResolver.resolveComponentFactory(ChoicemenuComponent);
    const viewContainerRef = this.viewContainerRef;

    const choicemenuComponentRef = viewContainerRef.createComponent(fcFactory);
    this.changeDetectorRef.detectChanges();

    return <ChoicemenuComponent>choicemenuComponentRef.instance;
  }

  public getContextMenuButtonsFromConfiguration(
    objectType: string | number,
    layerdefinition: MapLayer,
  ): Observable<ContextMenuButton[]> {
    return this.abcCore.objectSpace.getObjectType(objectType).pipe(
      map((ot) => {
        const ret: ContextMenuButton[] = [];
        for (const def of this.contextMenuConfiguration.definitions) {
          if (
            !def.objectTypes ||
            def.objectTypes.length === 0 ||
            def.objectTypes.findIndex((_) => ot.isMatchOrBase(_)) >= 0
          ) {
            for (const btn of def.buttons) {
              if (
                ret.findIndex((_) => _.name === btn.name) < 0 &&
                (!this.mapService.targetButtonNames?.find((name) => name === btn.name) || layerdefinition?.target?.url)
              ) {
                ret.push(btn);
              }
            }
          }
        }
        return ret;
      }),
    );
  }

  public getLayerContextMenuButtonsFromConfiguration(layerdefinition: MapLayer): ContextMenuButton[] {
    const ret: ContextMenuButton[] = [];
    for (const def of this.contextMenuConfiguration.definitions) {
      for (const btn of def.buttons) {
        if (
          ret.findIndex((_) => _.name === btn.name) < 0 &&
          this.mapService.targetButtonNames?.find((name) => name === btn.name) &&
          layerdefinition?.target?.url
        ) {
          ret.push(btn);
        }
      }
    }
    return ret;
  }

  private selectOneDataEntity(evt: any, coordinate: any): DataEntity {
    const clickedEntities: DataEntity[] = [];
    const clickedFeatures: DataEntity[] = [];

    this.map.forEachFeatureAtPixel(
      this.map.getEventPixel(evt),
      (feature: any, layer: any) => {
        if (layer?.layerdefinition?.selectable !== false) {
          if (
            layer?.layerdefinition?.type === MapLayerType[MapLayerType.Dbms] ||
            layer?.layerdefinition?.type === MapLayerType[MapLayerType.Shp]
          ) {
            const e = MapLoader.toLayerEntity(feature, layer?.layerdefinition);
            if (e && clickedFeatures.findIndex((_) => _.objectType === e.objectType && _.id === e.id) < 0) {
              if (e.feature) {
                e.feature.layerdefinition = layer.layerdefinition;
              }
              clickedFeatures.push(e);
            }
          } else {
            const e = MapLoader.toDataEntity(feature);
            if (e) {
              const found = clickedEntities.find((_) => _.id === e.id);
              if (found) {
                if (!found.feature?.layerdefinition?.target && layer.layerdefinition?.target) {
                  found.feature.layerdefinition = layer.layerdefinition;
                }
              } else {
                if (e.feature) {
                  e.feature.layerdefinition = layer.layerdefinition;
                }
                clickedEntities.push(e);
              }
            }
          }
        }
      },
      { hitTolerance: 10 },
    );

    if (clickedEntities.length > 0) {
      if (this.selectPath?.startEntity) {
        const idx = clickedEntities.findIndex((e) => e.objectType == this.selectPath.startEntity.objectType);
        if (idx > -1) return clickedEntities[idx];
        else return clickedEntities[0];
      } else {
        this.showEntityChoiceMenu(clickedEntities, clickedFeatures, coordinate);
        return null;
      }
    } else if (clickedFeatures.length > 0) {
      this.showEntityChoiceMenu(clickedEntities, clickedFeatures, coordinate);
    } else {
      if (!this.selectPath) {
        setTimeout(() => {
          this.mapService.navigateToObjects([]); //Force clear selection
        }, 500);
      }
      this.closeEntityChoiceMenu();
      return null;
    }
  }

  private closeEntityChoiceMenu() {
    this.resetCursor();
    if (this.choiceMenu) {
      this.choiceMenu.closePopup();
    }
  }

  private showEntityChoiceMenu(clickedDataEntities: DataEntity[], clickedLayerEntities: DataEntity[], coordinate: any) {
    if (!this.choiceMenu) {
      this.choiceMenu = this.loadChoiceMenu();
      this.choiceMenu.initMapOverlay(this.map);
      this.choiceMenu.controller = this;
    }
    this.choiceMenu.setVisible(false);
    this.choiceMenu.selectedObjects = [];

    const oldcursor = this.oldcursor;
    this.oldcursor = null;

    this.choiceMenu.fill(clickedDataEntities, clickedLayerEntities).subscribe(
      (categories) => {
        this.oldcursor = oldcursor;
        if (categories.length > 0) {
          if (categories.length == 1 && categories[0].items.length == 1) {
            this.updateContextMenu(
              categories[0].items[0].entity,
              ObjectHelper.isObject(clickedLayerEntities.find((_) => _ === categories[0].items[0].entity.entity)),
            ).subscribe(
              (_) => {
                this.resetCursor();
                this.openRadialMenu(categories[0].items[0].entity, coordinate);
              },
              (err) => {
                this.resetCursor();
              },
            );
          } else {
            const maxItemCount = 10;
            let itemsPerCategory = maxItemCount / categories.length;
            if (itemsPerCategory < 1) {
              itemsPerCategory = 1;
            }
            this.choiceMenu.selectedObjects = [];
            for (const cat of categories) {
              if (cat.items?.length > 0) {
                if (cat.items.length > itemsPerCategory) {
                  cat.items = cat.items.slice(0, itemsPerCategory);
                }
                for (const e of cat.items) {
                  ContextMenuController.updateButtonStates(this, e.buttons, e.entity);
                }
                this.choiceMenu.selectedObjects.push(cat);
              }
            }
            this.choiceMenu.lastCoordinate = coordinate;
            this.choiceMenu.setVisible(true);
            this.changeDetectorRef.detectChanges();
            this.choiceMenu.refreshPopup();
            this.choiceMenu.popup.setPosition(coordinate);
            this.resetCursor();
          }
        } else {
          this.resetCursor();
        }
      },
      (error) => {
        this.oldcursor = oldcursor;
        this.resetCursor();
        this.abcCore.error.publish(error);
      },
    );
  }

  private onObjectAction(selItem: any) {
    const btn: ContextMenuButton = selItem.button;
    const feature: any = selItem.obj.feature;

    if (btn.enabled) this.raiseMapAction(feature, btn);
  }

  private createPopup(contextMenu: ContextmenuComponent): any {
    const popup = new ol.Overlay({
      element: contextMenu.radialContainer.nativeElement,
      autoPan: true,
      autoPanAnimation: {
        duration: 250,
      },
    });

    return popup;
  }

  private updatePopup() {
    if (this.popup && this.currentContextMenu) {
      this.updateButtonStates();
    }
  }

  private updateButtonStates() {
    ContextMenuController.updateButtonStates(this, this.currentContextMenu?.buttons, this.currentEntity);
  }

  public static updateButtonStates(
    controller: ContextMenuController,
    buttons: ContextMenuButton[],
    entity: DataEntityInfo,
  ): void {
    if (buttons && entity) {
      for (const btn of buttons) {
        if (btn.enabledCondition) {
          btn.enabled = false;
          btn.enabledCondition(entity.entity).then(
            (_) => {
              if (_ === null && CacheStorageAdapter.useOfflineMode) {
                btn.enabled = true;
              } else {
                btn.enabled = _;
              }
              controller?.updateUI();
            },
            (err) => {
              btn.enabled = true;
              controller?.updateUI();
            },
          );
        }

        if (btn.badgeValueExpression) {
          const badgeValue = entity.commandsBadgeValue ? entity.commandsBadgeValue[btn.name] : null;
          if (badgeValue) {
            btn.badgeValue = badgeValue;
            btn.badgeHidden = !controller || btn.badgeValue == 0;
            if (btn.enabledByBadgeValue) {
              btn.enabled = !btn.badgeHidden;
            }
          } else {
            btn.badgeHidden = true;
            if (btn.enabledByBadgeValue) {
              btn.enabled = false;
            }
          }
          btn.badgeValueExpression(entity.entity).then(
            (_) => {
              btn.badgeValue = _ ?? badgeValue;
              btn.badgeHidden = !controller || btn.badgeValue == 0;
              if (btn.enabledByBadgeValue) {
                if (btn.badgeHidden && CacheStorageAdapter.useOfflineMode) {
                  btn.enabled = true;
                  if (btn.enabledCondition) {
                    btn.enabledCondition(entity.entity).then(
                      (_) => {
                        btn.enabled = _;
                        controller?.updateUI();
                      },
                      (err) => {
                        btn.enabled = true;
                        controller?.updateUI();
                      },
                    );
                  }
                } else {
                  btn.enabled = !btn.badgeHidden;
                }
              }
              controller?.updateUI();
            },
            (err) => {
              if (!badgeValue) {
                btn.badgeValue = null;
                btn.badgeHidden = true;
                if (btn.enabledByBadgeValue) {
                  btn.enabled = true;
                }
              }
              controller?.updateUI();
            },
          );
        }
      }
    }
  }

  private updateUI(): void {
    this.changeDetectorRef?.detectChanges();
    this.popup?.setElement(this.currentContextMenu.radialContainer.nativeElement);
    this.popup?.changed();
  }

  private closePopup(): boolean {
    this.resetCursor();
    if (this.popup) {
      if (!this.commandSelected && !this.selectPath) {
        this.mapService.navigateToObjects([]);
      }
      this.commandSelected = false;
      this.popup.setPosition(undefined);
    }

    return false;
  }

  private resetCursor(): void {
    if (this.oldcursor != null) {
      this.map.getTargetElement().style.cursor = this.oldcursor;
      this.oldcursor = null;
    }
  }

  public setActive(active: boolean) {
    this.active = active;
  }

  public isContextMenuVisible(): boolean {
    return (this.popup?.getPosition() && this.currentContextMenu?.isVisible()) || this.choiceMenu?.isVisible();
  }
}
